import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import { AvatarGroup, AvatarIndividual } from '@sas/react-svg';
import cx from 'classnames';
import { markEvent } from '../../marked-events';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { postfixTestId } from '../../util/testId';
import { Icon } from '../Icon';
import { getAvatarDataProvider } from './__internal__/AvatarDataProvider';
import { useAvatarA11yInfo } from './__internal__/useAvatarA11yInfo';
import { useOnAvatarUpdate } from './__internal__/useOnAvatarUpdate';
import cn from './Avatar.css.json';
import './Avatar.css';
import { jsx as _jsx } from "react/jsx-runtime";
const ICON_TYPES = {
  user: AvatarIndividual,
  group: AvatarGroup
};
const logger = getLogger('Avatar');
export const Avatar = /*#__PURE__*/forwardRef(function Avatar({
  dataProvider = getAvatarDataProvider(),
  ...props
}, ref) {
  const [isImageVisible, setIsImageVisible] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const {
    ariaLabel,
    tooltip,
    name
  } = useAvatarA11yInfo({
    ...props,
    dataProvider
  });
  const [fetchedTime, setFetchedTime] = useState();
  const [avatarSrc, setAvatarSrc] = useState();
  const fetchCounter = useRef(0);
  const size = props.size ?? 'medium';
  const avatarIcon = /*#__PURE__*/_jsx(Icon, {
    className: cn.avatarIcon,
    icon: ICON_TYPES[props.type ?? 'user'],
    tooltip: "",
    testId: postfixTestId(props.testId, 'icon')
  });
  const avatarImage = avatarSrc ? /*#__PURE__*/_jsx("img", {
    className: cx(cn.avatarImage, !isImageVisible && cn.imageInvisible),
    "data-testid": postfixTestId(props.testId, 'image'),
    src: avatarSrc,
    alt: "",
    onError: () => setIsImageVisible(false),
    onLoad: () => setIsImageVisible(true)
  }) : null;
  let avatar;
  if (props.type === 'group') {
    avatar = avatarImage ?? avatarIcon;
  } else {
    if (name === undefined || avatarSrc === undefined) {
      // Still waiting for responses from Identities Services
      avatar = null;
    } else if (avatarImage) {
      avatar = avatarImage;
    } else if (name && name.length > 0) {
      // codePointAt will not return undefined because name has length > 1
      avatar = /*#__PURE__*/_jsx("div", {
        children: String.fromCodePoint(name.codePointAt(0))
      });
    } else {
      avatar = avatarIcon;
    }
  }
  useEffect(() => {
    return () => {
      // Increment the fetchCounter to discard the fetch response after unmounting
      fetchCounter.current += 1;
    };
  }, []);
  const updateShowCustomImage = useCallback(() => {
    const fetchCount = ++fetchCounter.current;
    dataProvider.getAvatar?.(props.userId, props.type).then(src => {
      if (fetchCounter.current > fetchCount) return;
      setAvatarSrc(src);
    }).catch(() => {
      if (fetchCounter.current > fetchCount) return;
      setAvatarSrc('');
      logger.warn(`Failed to fetch the avatar details info for id:${props.userId} and type: ${props.type}. Using default avatar.`);
    }).finally(() => {
      if (fetchCounter.current > fetchCount) return;
      setFetchedTime(Date.now());
    });
  }, [dataProvider, props.userId, props.type]);
  useOnAvatarUpdate(dataProvider, updateShowCustomImage, props.userId, props.type, fetchedTime);
  useEffect(updateShowCustomImage, [updateShowCustomImage]);

  // Get design tokens
  const dt = useDesignTokens();
  const tokens = {
    ...renderInlineCssVars(getAvatarTokens(dt), 'avatar')
  };
  const focusable = props.focusable ?? (props.useAsButton ? true : undefined);
  return /*#__PURE__*/_jsx("div", {
    id: props.id,
    ref: ref,
    "data-testid": props.testId,
    className: cx(cn[size], cn.avatar, props.className),
    style: {
      ...tokens,
      ...props.style
    },
    "aria-label": ariaLabel,
    "aria-haspopup": props.ariaHasPopup,
    "aria-hidden": props.ariaLabel === '' ? true : undefined,
    title: tooltip,
    role: props.useAsButton ? 'button' : 'img'
    // Set to -1 if focusable is explicitly set to false.
    ,
    tabIndex: focusable === undefined ? undefined : focusable ? 0 : -1,
    onClick: () => {
      props.useAsButton && props.onPress?.();
    },
    onKeyDown: e => {
      if (props.useAsButton && (e.key === 'Enter' || e.key === ' ')) {
        markEvent(e);
        setIsActive(true);
      }
    },
    onKeyUp: e => {
      if (props.useAsButton && (e.key === 'Enter' || e.key === ' ')) {
        setIsActive(false);
      }
    },
    onKeyPress: e => {
      if (props.useAsButton && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        props.onPress?.();
      }
    },
    onBlur: () => setIsActive(false),
    children: /*#__PURE__*/_jsx("div", {
      className: cx(cn.innerContainer, avatar?.type === 'img' && cn.hasImage, props.useAsButton && cn.avatarButton, isActive && cn.active),
      "aria-hidden": "true",
      children: avatar
    })
  });
});
const getAvatarTokens = dt => {
  return {
    'font-size-s': dt.tokens['size-icon-m'],
    'font-size-m': dt.tokens['size-icon-l'],
    'font-weight': dt.tokens['font-style-weight-700'],
    'text-transform-uppercase': dt.tokens['font-style-text-transform-uppercase'],
    'border-radius': dt.tokens['size-border-radius-full'],
    'active-bg': dt.tokens['color-action-background-emphasis-selected'],
    'active-fg': dt.tokens['color-action-text-emphasis-selected'],
    bw: dt.tokens['size-border-width-s'],
    bg: dt.tokens['color-action-background-emphasis-normal'],
    'hover-bg': dt.tokens['color-action-background-emphasis-hover'],
    'hover-fg': dt.tokens['color-action-text-emphasis-hover'],
    bd: dt.tokens['color-action-border-emphasis-normal'],
    fg: dt.tokens['color-action-text-emphasis-normal'],
    'focus-outline': 'var(--focus-light-bg)',
    'focus-outline-offset': 'calc(-1 * var(--focus-width))'
  };
};
//# sourceMappingURL=Avatar.js.map